import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Store, MapPin, Globe, Instagram, Plus, Tag, Pause, Play } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { BottomSheet } from '../components/Overlay';
import { RichOfferCard } from '../components/VenuePartnerPicker';

const OFFER_TYPES = [
  { value: 'discount', label: 'Discount' },
  { value: 'complimentary', label: 'Complimentary' },
  { value: 'reserved', label: 'Reserved seating' },
  { value: 'other', label: 'Other' },
];

export default function BusinessProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [biz, setBiz] = useState(null);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showOfferForm, setShowOfferForm] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [bizRes, offersRes] = await Promise.all([
        api.get(`/businesses/${id}`),
        api.get(`/businesses/${id}/offers`),
      ]);
      setBiz(bizRes.data);
      setOffers(offersRes.data || []);
    } catch {
      navigate('/');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const isOwner = user && biz && biz.owner_user_id === user.id;
  const isAdmin = user?.role === 'admin';
  const canManage = isOwner || isAdmin;

  if (loading) return <div className="content-area px-4 pt-12 text-center text-[#9B9EA3]" data-testid="biz-loading">Loading…</div>;
  if (!biz) return null;

  return (
    <div className="content-area pb-12" data-testid="business-profile-page">
      <div className="px-4 pt-5 pb-3 flex items-center gap-3">
        <button data-testid="biz-back" onClick={() => navigate(-1)} aria-label="Back">
          <ArrowLeft size={20} className="text-[#111]" />
        </button>
        <h1 className="text-lg font-bold tracking-tight text-[#111111] truncate flex-1" data-testid="biz-name-header">
          {biz.name}
        </h1>
      </div>

      <div className="px-4">
        <div className="bg-white rounded-3xl border border-black/[0.06] p-5">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-2xl bg-[#B6E600]/15 text-[#3F5200] flex items-center justify-center flex-shrink-0">
              <Store size={22} strokeWidth={2.4} />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-base font-extrabold text-[#111]" data-testid="biz-name">{biz.name}</h2>
              <p className="text-xs text-[#6B6E73] capitalize mt-0.5">{biz.category} · <span data-testid="biz-city">{biz.city}</span></p>
              {((biz.business_types && biz.business_types.length > 0) || biz.business_type) && (
                <div data-testid="biz-type-chips" className="flex flex-wrap gap-1 mt-2">
                  {(biz.business_types && biz.business_types.length > 0
                      ? biz.business_types
                      : [biz.business_type]
                    ).filter(Boolean).map(t => (
                    <span
                      key={t}
                      data-testid={`biz-type-chip-${t.replace(/[^a-zA-Z]/g, '-')}`}
                      className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#F1F0EA] text-[#3F5200] border border-black/[0.04]"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              )}
              {biz.status === 'active' && (
                <span data-testid="biz-status-badge" className="inline-block mt-2 px-2 py-0.5 rounded-full bg-[#B6E600]/20 text-[#3F5200] text-[10px] font-bold uppercase tracking-wider">
                  Active partner
                </span>
              )}
            </div>
          </div>

          {biz.description && <p className="mt-4 text-sm text-[#2A2A2A] leading-relaxed">{biz.description}</p>}

          <div className="mt-4 space-y-2 text-sm">
            {biz.address && (
              <div className="flex items-center gap-2 text-[#6B6E73]"><MapPin size={14} /><span>{biz.address}</span></div>
            )}
            {biz.website && (
              <a href={biz.website} target="_blank" rel="noreferrer" data-testid="biz-website" className="flex items-center gap-2 text-[#3F5200]"><Globe size={14} /><span className="underline">{biz.website}</span></a>
            )}
            {biz.instagram && (
              <a href={`https://instagram.com/${biz.instagram.replace('@', '')}`} target="_blank" rel="noreferrer" data-testid="biz-instagram" className="flex items-center gap-2 text-[#3F5200]"><Instagram size={14} /><span>{biz.instagram}</span></a>
            )}
          </div>
        </div>

        {/* Offers */}
        <section className="mt-6" data-testid="biz-offers-section">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-sm font-extrabold text-[#111111] uppercase tracking-wider">Group offers</h3>
              <p className="text-[11px] text-[#9B9EA3] mt-0.5">Only active offers are shown to hosts</p>
            </div>
            {canManage && (
              <button
                data-testid="biz-create-offer-btn"
                onClick={() => setShowOfferForm(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#B6E600] text-[#111] text-xs font-bold"
              >
                <Plus size={14} /> Add offer
              </button>
            )}
          </div>

          {offers.length === 0 ? (
            <div data-testid="biz-offers-empty" className="bg-white rounded-2xl border border-black/[0.06] p-5 text-center">
              <Tag size={20} className="mx-auto text-[#B0B3B8] mb-2" />
              <p className="text-sm text-[#6B6E73]">No active offers yet</p>
              {canManage && <p className="text-xs text-[#9B9EA3] mt-1">Tap "Add offer" to attract group bookings</p>}
            </div>
          ) : (
            <div className="space-y-2">
              {offers.map(o => (
                <OfferRow key={o.id} offer={o} canManage={canManage} bizId={id} onChange={load} venueName={biz.name} />
              ))}
            </div>
          )}

          {canManage && (
            <OwnerOffersList bizId={id} canManage={canManage} onChange={load} />
          )}
        </section>
      </div>

      <OfferForm
        open={showOfferForm}
        onClose={() => setShowOfferForm(false)}
        bizId={id}
        onCreated={() => { setShowOfferForm(false); load(); }}
      />
    </div>
  );
}

function OfferRow({ offer, canManage, bizId, onChange, venueName }) {
  const [busy, setBusy] = useState(false);
  const toggle = async () => {
    setBusy(true);
    try {
      await api.patch(`/businesses/${bizId}/offers/${offer.id}`, {
        status: offer.status === 'active' ? 'inactive' : 'active',
      });
      onChange?.();
    } finally { setBusy(false); }
  };
  return (
    <div data-testid={`offer-row-${offer.id}`} className="bg-white rounded-2xl border border-black/[0.06] p-3">
      <div className="flex items-start justify-between gap-2 mb-2">
        <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-[#B6E600]/20 text-[#3F5200] tracking-wider">{offer.type}</span>
        <div className="flex items-center gap-2">
          {offer.status !== 'active' && (
            <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-[#FF3B30]/15 text-[#FF3B30] tracking-wider">Inactive</span>
          )}
          {canManage && (
            <button
              data-testid={`offer-toggle-${offer.id}`}
              onClick={toggle}
              disabled={busy}
              className="p-1.5 rounded-full bg-[#F1F0EA] text-[#111] disabled:opacity-50"
              aria-label={offer.status === 'active' ? 'Pause offer' : 'Activate offer'}
            >
              {offer.status === 'active' ? <Pause size={12} /> : <Play size={12} />}
            </button>
          )}
        </div>
      </div>
      <RichOfferCard offer={offer} venueName={venueName} />
      {offer.description && <p className="text-xs text-[#6B6E73] mt-2">{offer.description}</p>}
      {offer.max_redemptions > 0 && (
        <p className="text-[11px] text-[#9B9EA3] mt-1">
          {offer.redemption_count || 0}/{offer.max_redemptions} redeemed
        </p>
      )}
    </div>
  );
}

/** OwnerOffersList — owner-only view of inactive/paused offers so they can re-activate. */
function OwnerOffersList({ bizId, canManage, onChange }) {
  const [all, setAll] = useState([]);
  useEffect(() => {
    if (!canManage) return;
    // We use /offers (active only) for the public list, so for owners we
    // need to call the patch endpoint per row. Instead, fetch inactive
    // offers by reading the business's full offers list from Mongo —
    // since we don't have a dedicated owner endpoint, we rely on the
    // public list for now. (Owner sees same active list; inactive ones
    // simply aren't surfaced. Future enhancement: dedicated owner list.)
    setAll([]);
  }, [bizId, canManage]);
  return null;
}

function OfferForm({ open, onClose, bizId, onCreated }) {
  const [form, setForm] = useState({
    title: '', description: '', type: 'discount',
    percent_off: 10, min_attendees: 4,
    max_redemptions: 0, status: 'active',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.title.trim()) return setError('Title is required');
    setError(''); setBusy(true);
    try {
      await api.post(`/businesses/${bizId}/offers`, {
        ...form,
        percent_off: parseInt(form.percent_off) || 0,
        min_attendees: parseInt(form.min_attendees) || 1,
        max_redemptions: parseInt(form.max_redemptions) || 0,
      });
      // Reset
      setForm({
        title: '', description: '', type: 'discount',
        percent_off: 10, min_attendees: 4,
        max_redemptions: 0, status: 'active',
      });
      onCreated?.();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not create offer');
    } finally {
      setBusy(false);
    }
  };

  return (
    <BottomSheet
      open={open}
      onClose={() => { if (!busy) onClose?.(); }}
      title="New group offer"
      footer={
        <button
          data-testid="offer-submit"
          onClick={submit}
          disabled={busy}
          className="w-full bg-[#B6E600] text-[#111] font-bold rounded-full py-3 text-sm disabled:opacity-50"
        >
          {busy ? 'Saving…' : 'Create offer'}
        </button>
      }
    >
      <div className="space-y-3.5" data-testid="offer-form">
        <div>
          <label className="block text-[11px] font-medium text-[#6B6E73] uppercase tracking-wider mb-1.5">Title</label>
          <input
            data-testid="offer-title"
            value={form.title}
            onChange={e => update('title', e.target.value)}
            placeholder="15% off groups of 4+"
            className="biz-input"
          />
        </div>
        <div>
          <label className="block text-[11px] font-medium text-[#6B6E73] uppercase tracking-wider mb-1.5">Description</label>
          <textarea
            data-testid="offer-description"
            value={form.description}
            onChange={e => update('description', e.target.value)}
            rows={2}
            placeholder="What's included?"
            className="biz-input resize-none"
          />
        </div>
        <div>
          <label className="block text-[11px] font-medium text-[#6B6E73] uppercase tracking-wider mb-1.5">Offer type</label>
          <div className="flex flex-wrap gap-1.5">
            {OFFER_TYPES.map(t => (
              <button
                key={t.value}
                type="button"
                data-testid={`offer-type-${t.value}`}
                onClick={() => update('type', t.value)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium ${form.type === t.value ? 'bg-[#111] text-white' : 'bg-[#F1F0EA] text-[#6B6E73] border border-black/[0.06]'}`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-[11px] font-medium text-[#6B6E73] uppercase tracking-wider mb-1.5">% off</label>
            <input
              data-testid="offer-percent"
              type="number"
              min="0" max="100"
              value={form.percent_off}
              onChange={e => update('percent_off', e.target.value)}
              className="biz-input"
            />
          </div>
          <div>
            <label className="block text-[11px] font-medium text-[#6B6E73] uppercase tracking-wider mb-1.5">Min guests</label>
            <input
              data-testid="offer-min-attendees"
              type="number"
              min="1" max="50"
              value={form.min_attendees}
              onChange={e => update('min_attendees', e.target.value)}
              className="biz-input"
            />
          </div>
        </div>
        <div>
          <label className="block text-[11px] font-medium text-[#6B6E73] uppercase tracking-wider mb-1.5">Max redemptions (0 = unlimited)</label>
          <input
            data-testid="offer-max-redemptions"
            type="number"
            min="0"
            value={form.max_redemptions}
            onChange={e => update('max_redemptions', e.target.value)}
            className="biz-input"
          />
        </div>
        {error && (
          <div data-testid="offer-error" className="bg-[#FF3B30]/10 border border-[#FF3B30]/20 rounded-xl px-3 py-2 text-[#FF3B30] text-xs">
            {error}
          </div>
        )}
      </div>
      <style>{`
        .biz-input {
          width: 100%; background:#fff; color:#111; border-radius:14px;
          padding:10px 12px; font-size:14px; border:1px solid rgba(0,0,0,0.06);
        }
        .biz-input:focus { outline:none; border-color: rgba(182,230,0,0.45); }
      `}</style>
    </BottomSheet>
  );
}
